/* camera-lag/meta.js — the capture-rate tags a phone writes, read without loading the video.

   mp4 / mov: `moov/meta` (handler `mdta`) holds `keys` + `ilst` — Android's MPEG4Writer puts
   `com.android.capture.fps` there (float32), iOS its `com.apple.quicktime.*` keys (make, model,
   full-frame-rate-playback-intent). Phones write `moov` at the END of the file, so the walk
   reads top-level box headers and jumps over `mdat` instead of reading it.
   WebM / Matroska: the tag survives a transcode as `COM.ANDROID.CAPTURE.FPS` in Segment\Tags —
   EbmlProbe (ebml-probe.js) owns that walk.

   CameraLagMeta.read(file) → Promise<{ captureFps?, fullFrameRateIntent?, make?, model? }>
   Never throws: a file it cannot read simply has no tags (the timebase then asks). */
(function (root) {
    'use strict';
    if (root.CameraLagMeta) return;

    function bytes(file, from, to) { return file.slice(from, to).arrayBuffer().then(function (b) { return new Uint8Array(b); }); }
    function u32(b, o) { return ((b[o] << 24) >>> 0) + (b[o + 1] << 16) + (b[o + 2] << 8) + b[o + 3]; }
    function type4(b, o) { return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]); }

    async function findMoov(file) {
        var pos = 0, size = file.size, guard = 0;
        while (pos + 8 <= size && guard++ < 64) {
            var h = await bytes(file, pos, Math.min(size, pos + 16));
            var len = u32(h, 0), t = type4(h, 4);
            if (len === 1 && h.length >= 16) len = u32(h, 8) * 4294967296 + u32(h, 12);
            else if (len === 0) len = size - pos;
            if (len < 8) return null;
            if (t === 'moov') return len > 64 * 1024 * 1024 ? null : bytes(file, pos, pos + len);
            pos += len;
        }
        return null;
    }
    function children(b, start, end, cb) {
        var p = start;
        while (p + 8 <= end) {
            var len = u32(b, p), t = type4(b, p + 4), hdr = 8;
            if (len === 1) { len = u32(b, p + 8) * 4294967296 + u32(b, p + 12); hdr = 16; }
            if (len < 8 || p + len > end) break;
            if (cb(t, p + hdr, p + len) === false) return;
            p += len;
        }
    }
    function readMdta(b, start, end, out) {
        // `meta` here is a plain box (QuickTime) — but some writers give it a full-box header
        var s = start;
        if (type4(b, s + 4) !== 'hdlr' && type4(b, s + 8) === 'hdlr') s += 4;
        var keys = [];
        children(b, s, end, function (t, cs, ce) {
            if (t === 'keys') {
                var n = u32(b, cs + 4), p = cs + 8;
                for (var i = 0; i < n && p + 8 <= ce; i++) {
                    var ks = u32(b, p);
                    var name = '';
                    for (var j = p + 8; j < p + ks; j++) name += String.fromCharCode(b[j]);
                    keys.push(name); p += ks;
                }
            }
        });
        children(b, s, end, function (t, cs, ce) {
            if (t !== 'ilst') return;
            children(b, cs, ce, function (it, is, ie) {
                var idx = ((it.charCodeAt(0) << 24) >>> 0) + (it.charCodeAt(1) << 16) + (it.charCodeAt(2) << 8) + it.charCodeAt(3);
                var key = keys[idx - 1]; if (!key) return;
                children(b, is, ie, function (dt, ds, de) {
                    if (dt !== 'data') return;
                    var wellKnown = u32(b, ds) & 0xffffff, v = ds + 8, n = de - v, val = null;
                    if (wellKnown === 23 && n >= 4) val = new DataView(b.buffer, b.byteOffset + v, 4).getFloat32(0, false);
                    else if (wellKnown === 24 && n >= 8) val = new DataView(b.buffer, b.byteOffset + v, 8).getFloat64(0, false);
                    else if ((wellKnown === 21 || wellKnown === 22 || wellKnown === 75 || wellKnown === 76) && n >= 1 && n <= 8) {
                        val = 0; for (var q = 0; q < n; q++) val = val * 256 + b[v + q];
                    } else if (wellKnown === 1) {
                        try { val = new TextDecoder('utf-8').decode(b.subarray(v, de)); } catch (e) { val = null; }
                    }
                    if (val != null) out[key.toLowerCase()] = val;
                });
            });
        });
    }
    async function readMp4(file) {
        var moov = await findMoov(file);
        var raw = {};
        if (!moov) return raw;
        children(moov, 8, moov.length, function (t, cs, ce) {
            if (t === 'meta') readMdta(moov, cs, ce, raw);
            if (t === 'udta') children(moov, cs, ce, function (t2, c2, e2) { if (t2 === 'meta') readMdta(moov, c2, e2, raw); });
        });
        return raw;
    }
    async function readMkv(file) {
        if (!root.EbmlProbe) return {};
        try {
            var r = await root.EbmlProbe.read(file), raw = {};
            var tags = (r && r.tags) || {};
            Object.keys(tags).forEach(function (k) { raw[String(k).toLowerCase()] = tags[k]; });
            return raw;
        } catch (e) { return {}; }
    }
    function norm(raw) {
        var out = {};
        var cf = raw['com.android.capture.fps'];
        if (cf != null && isFinite(parseFloat(cf))) out.captureFps = parseFloat(cf);
        var fi = raw['com.apple.quicktime.full-frame-rate-playback-intent'];
        if (fi != null && isFinite(+fi)) out.fullFrameRateIntent = +fi;
        if (raw['com.apple.quicktime.make']) out.make = String(raw['com.apple.quicktime.make']);
        if (raw['com.apple.quicktime.model']) out.model = String(raw['com.apple.quicktime.model']);
        return out;
    }
    async function read(file) {
        try {
            var isMkv = /\.(webm|mkv)$/i.test(file.name || '') || /webm|matroska/i.test(file.type || '');
            return norm(isMkv ? await readMkv(file) : await readMp4(file));
        } catch (e) { return {}; }
    }
    root.CameraLagMeta = { read: read, _readMp4: readMp4 };
})(typeof self !== 'undefined' ? self : this);
